'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const BRAND = '#3757FF';

export default function StudioCTA() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#fff' }}>
            <div className="max-w-7xl mx-auto relative z-10">
                {/* Main CTA banner */}
                <div
                    className={`rounded-3xl p-10 md:p-16 relative overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                    style={{ background: BRAND, boxShadow: '0 32px 80px rgba(55,87,255,0.3)' }}
                >
                    {/* Background orbs */}
                    <div className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
                    <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', transform: 'translate(-20%, 20%)' }} />

                    <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <p className="text-xs font-700 uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.15em' }}>Get started with Studio</p>
                            <h2
                                className="font-display font-800 text-white mb-4"
                                style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em' }}
                            >
                                Ready to run your
                                <br />
                                educational operation?
                            </h2>
                            <p className="text-base mb-8" style={{ color: 'rgba(255,255,255,0.75)' }}>
                                Whether you're an independent tutor or a full institution, Wisly Studio gives you the workspace to teach, manage, and grow.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <a
                                    href="/early-access"
                                    className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-sm font-700 transition-all duration-300"
                                    style={{ background: 'white', color: BRAND }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                                >
                                    Request early access
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M2.5 7h9M7.5 3.5l3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </a>
                                <a
                                    href="/contact"
                                    className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-sm font-600 transition-all duration-300"
                                    style={{ color: 'white', border: '1.5px solid rgba(255,255,255,0.3)' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.1)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                >
                                    Schedule a demo
                                </a>
                            </div>
                        </div>

                        {/* Feature checklist */}
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                'Schedule management',
                                'Live classrooms',
                                'Student roster',
                                'Assessment builder',
                                'Staff management',
                                'Analytics dashboard',
                                'Billing & payments',
                                'Content library',
                            ].map((feature) => (
                                <div key={feature} className="flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                            <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <span className="text-xs font-600 text-white">{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom nav */}
                <div className={`mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <p className="text-sm font-600" style={{ color: '#888' }}>
                        Looking for the learner experience?
                    </p>
                    <Link
                        href="/learn"
                        className="inline-flex items-center gap-2 text-sm font-700 transition-colors"
                        style={{ color: BRAND }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.75'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                    >
                        Explore Wisly Learn
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2.5 7h9M7.5 3.5l3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Link>
                </div>
            </div>
        </section>
    );
}
